/** One side's grade, and the overall Lead Score, as the scoring engine spells them. */
export type ScoreTier = "P1" | "P2" | "P3" | "DQ";

/** Rows of the explainer grid: the company score, best first. */
export const COMPANY_TIERS: readonly ScoreTier[] = ["P1", "P2", "P3", "DQ"];

/** Columns of the explainer grid: the persona score, worst first, as the mockup draws it. */
export const PERSONA_TIERS: readonly ScoreTier[] = ["DQ", "P3", "P2", "P1"];

const GRADED: readonly ScoreTier[] = ["P1", "P2", "P3"];

/**
 * Which overall Lead Scores a company grade and a persona grade can produce
 * (PREC-4079), read off the engine rather than off the mockup.
 *
 * `createLeadScoring` does not combine the two grades — it adds the raw points.
 * The company total is banded against the thirds of what the company criteria
 * can award (`C`), the persona total against the thirds of `P`, and the overall
 * score against the two bands added together. So, writing `c` and `p` for the
 * totals:
 *
 * - Both sides agree: both ≤ ⅓ of their maximum puts `c + p` ≤ ⅓(C + P) — P3.
 *   Both in the middle third puts the sum in the middle third; both in the top
 *   third puts it in the top third. An agreeing pair therefore pins the overall
 *   score to that same grade.
 * - The sides differ: the sum lands somewhere between them, and *where* depends
 *   on the totals and on how much each side can award at most. A company P1 with
 *   a persona P3 comes out P1 when the company criteria carry most of the points
 *   and P3 when the persona criteria do — so the honest answer for that corner is
 *   the whole range, not one grade.
 * - A disqualifying criterion on either side disqualifies the lead outright,
 *   before any of this.
 *
 * The result is the inclusive range between the two grades: one entry when they
 * agree, the grades in between when they do not.
 */
export const combineTiers = (company: ScoreTier, persona: ScoreTier): ScoreTier[] => {
	if (company === "DQ" || persona === "DQ") {
		return ["DQ"];
	}

	const best = Math.min(GRADED.indexOf(company), GRADED.indexOf(persona));
	const worst = Math.max(GRADED.indexOf(company), GRADED.indexOf(persona));

	return GRADED.slice(best, worst + 1);
};

/** The two inclusive upper edges a total is banded against: the top of P3, then the top of P2. */
export type TierEdges = readonly [p3Ends: number, p2Ends: number];

/** Where one side's thirds fall, as `createLeadScoring` rounds them. Both edges are inclusive. */
export const sideEdges = (max: number): TierEdges => [
	Math.round(max / 3),
	Math.round((max / 3) * 2),
];

/**
 * Where the overall thirds fall. The engine does not band the sum against a
 * third of the combined maximum — it adds the two sides' *edges*, so a side that
 * can award more points also moves the overall edges further.
 */
const overallEdges = (maxCompany: number, maxPersona: number): TierEdges => {
	const [companyP3, companyP2] = sideEdges(maxCompany);
	const [personaP3, personaP2] = sideEdges(maxPersona);

	return [companyP3 + personaP3, companyP2 + personaP2];
};

/** The grade a total lands in, given the edges it is banded against. */
const tierFromEdges = (points: number, [p3Ends, p2Ends]: TierEdges): ScoreTier => {
	if (points <= p3Ends) {
		return "P3";
	}

	return points <= p2Ends ? "P2" : "P1";
};

/** Which third of its own maximum a side's point total lands in. */
export const tierOf = (points: number, max: number): ScoreTier =>
	tierFromEdges(points, sideEdges(max));

/** The overall grade for a pair of totals. */
export const overallTierOf = (
	companyPoints: number,
	personaPoints: number,
	maxCompany: number,
	maxPersona: number
): ScoreTier => tierFromEdges(companyPoints + personaPoints, overallEdges(maxCompany, maxPersona));

/** One grade's inclusive point range — the borders `tierOf` grades against, spelled out. */
export interface TierBand {
	tier: ScoreTier;
	from: number;
	to: number;
}

/**
 * The three grades as point ranges, worst first, derived from the same edges the
 * grading uses so the two can never disagree. A range the rounding leaves empty
 * (possible only for a side that can award one or two points at most) is dropped.
 */
const bandsFromEdges = ([p3Ends, p2Ends]: TierEdges, max: number): TierBand[] =>
	(
		[
			{ tier: "P3", from: 0, to: p3Ends },
			{ tier: "P2", from: p3Ends + 1, to: p2Ends },
			{ tier: "P1", from: p2Ends + 1, to: max },
		] as const
	)
		.filter((band) => band.to >= band.from)
		.map((band) => ({ ...band }));

/** The point ranges of P3/P2/P1 for one side that can award `max` points. */
export const bandsOf = (max: number): TierBand[] => bandsFromEdges(sideEdges(max), max);

/** The point ranges of P3/P2/P1 for the overall Lead Score of the two sides. */
export const overallBandsOf = (maxCompany: number, maxPersona: number): TierBand[] =>
	bandsFromEdges(overallEdges(maxCompany, maxPersona), maxCompany + maxPersona);

/** One criterion of the worked example: what it awarded, and what it could have. */
export interface ExampleCriterion {
	/** i18n key under `leadScoring.how.example`. */
	label: string;
	points: number;
	max: number;
}

/**
 * The example the explainer walks through (PREC-4079). It is deliberately a case
 * where the two sides disagree — a strong company, a weak person — so it lands in
 * one of the grid's range cells and shows what decides it: the company criteria
 * can award more here (9 against 6), and they are nearly full, so the lead comes
 * out P2 rather than P3.
 */
export const SCORE_EXAMPLE: { company: ExampleCriterion[]; persona: ExampleCriterion[] } = {
	company: [
		{ label: "hq", points: 3, max: 3 },
		{ label: "size", points: 2, max: 3 },
		{ label: "ai", points: 3, max: 3 },
	],
	persona: [
		{ label: "seniority", points: 1, max: 3 },
		{ label: "location", points: 0, max: 3 },
	],
};

/** Points awarded and points possible for one side of the example. */
export const totalOf = (criteria: ExampleCriterion[]): { points: number; max: number } => ({
	points: criteria.reduce((sum, criterion) => sum + criterion.points, 0),
	max: criteria.reduce((sum, criterion) => sum + criterion.max, 0),
});
